import React, { useEffect, useRef, useState } from "react";
import { WidgetType } from "../../types/widget-type";
import { convertWidgetToHtmlString } from "~/utils/transform";
import CollectEmail from "~/components/CollectEmail";
import { useApollo } from "~/utils/apollo-client";
import { QUERY_GET_CMS_BLOCKS } from "~/data/home";

type Props = {
  inputString: string;
  prerenderedHtml?: string;
};

const BlockByWidgetType = ({ inputString, prerenderedHtml }: Props) => {
  if (prerenderedHtml) {
    return <PrerenderedWidget html={prerenderedHtml} />;
  }
  return <ClientResolvedWidget inputString={inputString} />;
};

// SSR path: the resolved HTML (incl. third-party embed placeholders) is in the
// initial document. We render it via dangerouslySetInnerHTML so React doesn't
// own the subtree — anything a vendor script injects into placeholder divs
// (e.g. emk forms) is safe from React's reconciler on subsequent re-renders.
//
// Wrapped in React.memo so parent re-renders never cause a reconcile of this
// subtree. Without this, parent updates (Recoil/Apollo) on busy pages like the
// homepage can cause React to discard the wrapper div and recreate it,
// destroying anything embed scripts had injected.
const PrerenderedWidget = React.memo(
  ({ html }: { html: string }) => {
    const formRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const swap = () => {
        if (!formRef.current) return false;
        const placeholder = formRef.current.querySelector("#submit-email-form");
        if (!placeholder) return false;
        const form = document.querySelector("#email-form");
        if (!form) return false;
        placeholder.replaceWith(form);
        return true;
      };

      if (swap()) return;

      const intervalId = setInterval(() => {
        if (swap()) clearInterval(intervalId);
      }, 100);
      const timeoutId = setTimeout(() => clearInterval(intervalId), 10000);
      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }, []);

    return (
      <>
        <div ref={formRef} dangerouslySetInnerHTML={{ __html: html }} />
        <div className="hidden">
          <CollectEmail />
        </div>
      </>
    );
  },
  (prev, next) => prev.html === next.html
);

const ClientResolvedWidget = ({ inputString }: { inputString: string }) => {
  const [data, setData] = useState<string | null>(null);
  const apolloClient = useApollo(undefined);
  const formRef = useRef(null);

  const getComponentByType = async (type: WidgetType) => {
    const { data } = await apolloClient.query({
      query: QUERY_GET_CMS_BLOCKS,
      variables: {
        identifiers: [type],
      },
      context: { fetchOptions: { method: "GET" } },
    });
    switch (type) {
      case WidgetType.COLLECT_EMAIL:
        return data?.cmsBlocks?.items?.[0]?.content ?? "";
      default:
        return '<div id="submit-email-form"></div>';
    }
  };

  useEffect(() => {
    const getData = async () => {
      const data = await convertWidgetToHtmlString(
        inputString,
        getComponentByType
      );
      setData(data);
    };

    getData();
  }, [convertWidgetToHtmlString, inputString]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Check if the div with id 'replaceMe' exists in the DOM
      if (formRef.current) {
        const divToReplace = (formRef.current as any).querySelector(
          "#submit-email-form"
        );

        if (divToReplace) {
          // Replace the div with new content
          const newElement = document.querySelector("#email-form");
          divToReplace.replaceWith(newElement);

          // Clear the interval once the div is found and replaced
          clearInterval(intervalId);
        }
      }
    }, 100); // Check every 100ms

    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      console.error("Failed to find #submit-email-form within 10 seconds");
    }, 10000);

    // Cleanup the interval when the component is unmounted
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  if (!data) return null;

  return (
    <>
      <div
        dangerouslySetInnerHTML={{
          __html: data,
        }}
        ref={formRef}
      />
      <div className="hidden">
        <CollectEmail />
      </div>
    </>
  );
};

export default BlockByWidgetType;
